/**
 * This function calculates the number of lines to allocate to each given field based on the total number of lines each is requesting,
 * and the available space. Assumes that the first line is most important, and will try to allocate as much space as possible while
 * still showing at least one line of each other field.
 * @param {number[]} lineHeights - the line height for every line
 * @param {number} totalHeight - the total amount of available space
 * @param {number[]} fieldHeights - the desired height for every field
 * @returns the number of lines to allow for each field, in order provided
 */
export default function calculateFieldSizings(
    lineHeights: number[],
    totalHeight: number,
    fieldHeights: number[]
): number[] {
    let fieldLines = fieldHeights.map((height, index) => {
        if (height >= lineHeights[index] - 2 && height <= lineHeights[index] + 1) {
            height = lineHeights[index];
        }
        if (height < lineHeights[index]) {
            return 0;
        }
        return Math.ceil(height / lineHeights[index]);
    });

    // first we see how much total space we have, and how many of the fields we would be able to get some text for
    // if we only allocated one line to each field -- the last one we have space for becomes the "least important field"
    // that we'll start with
    let totalSpaceRequested = 0;
    let leastImportantFieldIndex = fieldLines.length - 1;
    for (let i = 0; i < fieldLines.length; i++) {
        totalSpaceRequested += lineHeights[i];
        if (totalSpaceRequested > totalHeight) {
            leastImportantFieldIndex = i - 1;
            fieldLines = fieldLines.map((line, index) =>
                index > leastImportantFieldIndex ? 0 : line
            );
            break;
        }
    }

    // starting from least important field, shrink each to 1 (or 0 if not requested), stopping at the last field
    for (let i = leastImportantFieldIndex; i > 0; i--) {
        let totalSpaceRequested = 0;
        fieldLines.forEach((lines, index) => (totalSpaceRequested += lines * lineHeights[index]));
        if (totalSpaceRequested > totalHeight) {
            fieldLines[i] = Math.min(fieldLines[i], 1);
        }
    }

    // now the last field gets all the remaining available space
    let totalSpaceRequestedExceptFirst = 0;
    fieldLines.forEach(
        (lines, index) =>
            (totalSpaceRequestedExceptFirst += index == 0 ? 0 : lines * lineHeights[index])
    );
    fieldLines[0] = Math.min(
        fieldLines[0],
        Math.floor((totalHeight - totalSpaceRequestedExceptFirst) / lineHeights[0])
    );

    return fieldLines;
}
